import { useState, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Button,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from '@/components/ui';
import { Copy, Check, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { Payment, SortConfig } from '../types';
import PackageBadge from './PackageBadge';
import ProviderBadge from './ProviderBadge';
import EmptyFilteredState from './EmptyFilteredState';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';

type SortableColumn = 'createdAt' | 'priceInEur' | 'packageName' | 'provider';

interface PaymentsTableProps {
    payments: Payment[];
    sortConfig: SortConfig;
    onSort: (key: SortableColumn, order: 'asc' | 'desc') => void;
    showEmptyFilteredState?: boolean;
}

/**
 * Desktop table view for payments history.
 * Features sortable columns, copy-to-clipboard for transaction IDs, and stagger animations.
 */
const PaymentsTable = ({ payments, sortConfig, onSort, showEmptyFilteredState = false }: PaymentsTableProps) => {
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const handleCopy = useCallback(async (txId: string) => {
        try {
            await navigator.clipboard.writeText(txId);
            setCopiedId(txId);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }, []);

    const truncateId = (id: string, maxLength: number = 16) => {
        if (id.length <= maxLength) return id;
        return `${id.slice(0, maxLength)}...`;
    };

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'd MMM yyyy', { locale: fr });
        } catch {
            return dateString;
        }
    };

    const formatCurrency = (amount: number, currency: string) => {
        if (currency === 'EUR') return `€${amount.toFixed(2)}`;
        if (currency === 'USD') return `$${amount.toFixed(2)}`;
        return `${amount.toFixed(2)} ${currency}`;
    };

    const SortIcon = ({ columnKey }: { columnKey: SortableColumn }) => {
        if (sortConfig.key !== columnKey) {
            return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp className="w-4 h-4 ml-1" />
            : <ChevronDown className="w-4 h-4 ml-1" />;
    };

    const SortableHeader = ({
        columnKey,
        children
    }: {
        columnKey: SortableColumn;
        children: React.ReactNode
    }) => {
        const isActive = sortConfig.key === columnKey;
        const currentOrder = isActive ? sortConfig.direction : undefined;

        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className="flex items-center hover:text-foreground transition-colors w-full justify-start"
                    >
                        {children}
                        <SortIcon columnKey={columnKey} />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem
                        onClick={() => onSort(columnKey, 'asc')}
                        className={cn(
                            'cursor-pointer',
                            currentOrder === 'asc' && 'bg-muted font-semibold'
                        )}
                    >
                        <ChevronUp className="w-4 h-4 mr-2" />
                        Croissant
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => onSort(columnKey, 'desc')}
                        className={cn(
                            'cursor-pointer',
                            currentOrder === 'desc' && 'bg-muted font-semibold'
                        )}
                    >
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Décroissant
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    };

    return (
        <TooltipProvider>
            <div className="rounded-md border overflow-x-auto">
                <Table className="table-fixed">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[20%]">
                                <SortableHeader columnKey="createdAt">Date</SortableHeader>
                            </TableHead>
                            <TableHead className="w-[20%]">
                                <SortableHeader columnKey="packageName">Package</SortableHeader>
                            </TableHead>
                            <TableHead className="w-[20%]">
                                <SortableHeader columnKey="priceInEur">Montant</SortableHeader>
                            </TableHead>
                            <TableHead className="w-[20%]">
                                <SortableHeader columnKey="provider">Fournisseur</SortableHeader>
                            </TableHead>
                            <TableHead className="w-[20%]">Transaction ID</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {showEmptyFilteredState && payments.length === 0 ? (
                            <EmptyFilteredState />
                        ) : (
                            payments.map((payment, index) => (
                            <TableRow
                                key={payment._id}
                                className={cn(
                                    'animate-in fade-in-0 slide-in-from-bottom-2',
                                    'hover:bg-muted/50 transition-colors'
                                )}
                                style={{
                                    animationDelay: `${index * 50}ms`,
                                    animationFillMode: 'both'
                                }}
                            >
                                <TableCell className="font-medium">
                                    {formatDate(payment.createdAt)}
                                </TableCell>
                                <TableCell>
                                    <PackageBadge packageName={payment.packageName} />
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <span className="font-semibold">€{payment.priceInEur.toFixed(2)}</span>
                                        {payment.currency !== 'EUR' && (
                                            <span className="text-xs text-muted-foreground">
                                                ({formatCurrency(payment.price, payment.currency)})
                                            </span>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <ProviderBadge provider={payment.provider} />
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                            {truncateId(payment.transactionId)}
                                        </code>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    onClick={() => handleCopy(payment.transactionId)}
                                                >
                                                    {copiedId === payment.transactionId ? (
                                                        <Check className="h-3 w-3 text-green-500" />
                                                    ) : (
                                                        <Copy className="h-3 w-3" />
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {copiedId === payment.transactionId ? 'Copié !' : 'Copier'}
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </TooltipProvider>
    );
};

export default PaymentsTable;
